import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { Plus, Edit3, Trash2, KeyRound, X, Save, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function Operators() {
  const { api } = useTenant();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/operators")
      .then(r => setList(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleActive = async (op) => {
    try {
      await api.patch(`/admin/operators/${op.id}`, { active: !op.active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const onDelete = async (op) => {
    if (!window.confirm(`Delete operator ${op.name}?`)) return;
    try { await api.delete(`/admin/operators/${op.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Operators</h1>
          <p className="text-sm text-slate-400">Manage who can log in to the iPad field app at <code className="text-amber-300">/app</code>. PINs are 4–6 digits.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-operator"
                className="bg-emerald-500 hover:bg-emerald-400 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center gap-1.5">
          <Plus size={16} /> Add operator
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Initials</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map(o => (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`op-row-${o.id}`}>
                  <td className="px-4 py-3 font-medium">{o.name}</td>
                  <td className="px-4 py-3 text-amber-300 font-bold tabular-nums">{o.initials}</td>
                  <td className="px-4 py-3 text-slate-400">{o.role}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(o)} className={`flex items-center gap-1 text-xs font-bold ${o.active ? "text-emerald-300" : "text-slate-500"}`}>
                      {o.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                      {o.active ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(o)} className="text-slate-300 hover:text-amber-300 p-1.5"><Edit3 size={14} /></button>
                    <button onClick={() => onDelete(o)} className="text-slate-300 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!list.length && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No operators yet — click “Add operator”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(showAdd || editing) && (
        <OperatorModal op={editing}
                       onClose={() => { setShowAdd(false); setEditing(null); }}
                       onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
                       api={api} />
      )}
    </div>
  );
}

function OperatorModal({ op, onClose, onSaved, api }) {
  const editing = !!op;
  const [name, setName] = useState(op?.name || "");
  const [pin, setPin]   = useState("");
  const [role, setRole] = useState(op?.role || "operator");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const save = async () => {
    if (!name) { setErr("Name is required"); return; }
    if (!editing && !pin) { setErr("PIN is required"); return; }
    if (pin && !/^\d{4,6}$/.test(pin)) { setErr("PIN must be 4-6 digits"); return; }
    setBusy(true); setErr(null);
    try {
      if (editing) {
        const payload = { name, role };
        if (pin) payload.pin = pin;
        await api.patch(`/admin/operators/${op.id}`, payload);
        toast.success("Operator updated");
      } else {
        await api.post("/admin/operators", { name, pin, role });
        toast.success("Operator added");
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0b1020] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">{editing ? `Edit ${op.name}` : "Add operator"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} data-testid="op-name"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">
              <KeyRound size={11} className="inline mr-1" /> PIN {editing && <span className="text-slate-500 normal-case">(leave blank to keep current)</span>}
            </label>
            <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
                   maxLength={6} inputMode="numeric" data-testid="op-pin"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white tracking-widest text-lg font-bold" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)} data-testid="op-role"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
              <option value="operator">Operator (driver)</option>
              <option value="foreman">Foreman</option>
              <option value="guest">Guest</option>
            </select>
          </div>
          {err && <div className="text-sm text-red-300">{err}</div>}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="op-save"
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-1.5">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
